import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Clock, Calendar } from 'lucide-react';

interface MenteeCardProps {
  mentee: {
    id: number;
    first_name: string;
    last_name: string;
    admin_verified_at: string;
    days_waiting: number;
    is_new: boolean;
  };
  onViewProfile: (menteeId: number) => void;
}

export function MenteeCard({ mentee, onViewProfile }: MenteeCardProps) {
  const formatWaitingTime = (days: number) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return '1 week ago';
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 60) return '1 month ago';
    return `${Math.floor(days / 30)} months ago`;
  };

  const getUrgencyColor = (days: number) => {
    if (days <= 7) return 'text-green-600 bg-green-50';
    if (days <= 14) return 'text-yellow-600 bg-yellow-50';
    if (days <= 30) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <Card className="hover:shadow-lg transition-shadow relative">
      {mentee.is_new && (
        <Badge className="absolute -top-2 -right-2 bg-primary text-white">
          New
        </Badge>
      )}
      
      <CardHeader className="pb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold">
              {mentee.first_name} {mentee.last_name[0]}.
            </h3>
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Calendar className="w-3 h-3" />
              <span>Approved {formatWaitingTime(mentee.days_waiting)}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Waiting indicator for urgent cases */}
        {mentee.days_waiting > 14 && (
          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getUrgencyColor(mentee.days_waiting)}`}>
            <Clock className="w-3 h-3" />
            <span>Waiting {mentee.days_waiting} days</span>
          </div>
        )}
        
        <Button
          className="w-full"
          size="sm"
          onClick={() => onViewProfile(mentee.id)}
        >
          View Profile
        </Button>
      </CardContent>
    </Card>
  );
}
